// ─── Trading Indicators Library ───
// All calculations are based on close prices from candlestick data

export interface CandleData {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export interface TrendbarRaw {
  timestamp?: number;
  low?: number;
  high?: number;
  open?: number;
  close?: number;
  volume?: number;
}

export interface IndicatorResult {
  values: (number | null)[]; // same length as input, null for periods where not enough data
  label: string;
  color: string;
}

export interface BollingerResult {
  middle: (number | null)[];
  upper: (number | null)[];
  lower: (number | null)[];
  bandwidth: (number | null)[]; // (upper - lower) / middle * 100
}

// ─── SMA — Simple Moving Average ─────────────────────────────────

export function SMA(values: number[], period: number): (number | null)[] {
  if (period <= 0 || values.length === 0) return values.map(() => null);

  const result: (number | null)[] = new Array(values.length).fill(null);
  let sum = 0;

  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= period) {
      sum -= values[i - period];
    }
    if (i >= period - 1) {
      result[i] = sum / period;
    }
  }

  return result;
}

// ─── WMA — Weighted Moving Average ───────────────────────────────

export function WMA(values: number[], period: number): (number | null)[] {
  if (period <= 0 || values.length === 0) return values.map(() => null);

  const result: (number | null)[] = new Array(values.length).fill(null);
  const weightSum = (period * (period + 1)) / 2;

  for (let i = period - 1; i < values.length; i++) {
    let wSum = 0;
    for (let j = 0; j < period; j++) {
      wSum += values[i - period + 1 + j] * (j + 1);
    }
    result[i] = wSum / weightSum;
  }

  return result;
}

// ─── HMA — Hull Moving Average ───────────────────────────────────
// HMA(n) = WMA(2 * WMA(n/2) - WMA(n), sqrt(n))

export function HMA(values: number[], period: number): (number | null)[] {
  if (period <= 0 || values.length === 0) return values.map(() => null);

  const halfPeriod = Math.floor(period / 2);
  const sqrtPeriod = Math.floor(Math.sqrt(period));

  if (halfPeriod < 1 || sqrtPeriod < 1) return values.map(() => null);

  // WMA with half period
  const wmaHalf = WMA(values, halfPeriod);
  // WMA with full period
  const wmaFull = WMA(values, period);

  // Intermediate: 2 * WMA(n/2) - WMA(n)
  const diffSeries: number[] = [];
  const validFrom = period - 1; // aligned to wmaFull's first valid index
  for (let i = 0; i < values.length; i++) {
    if (i >= validFrom && wmaHalf[i] !== null && wmaFull[i] !== null) {
      diffSeries.push(2 * wmaHalf[i]! - wmaFull[i]!);
    } else {
      diffSeries.push(NaN);
    }
  }

  // Extract only valid diff values for the final WMA
  const cleanDiff: number[] = [];
  const cleanIndices: number[] = [];
  for (let i = 0; i < diffSeries.length; i++) {
    if (!isNaN(diffSeries[i])) {
      cleanDiff.push(diffSeries[i]);
      cleanIndices.push(i);
    }
  }

  // WMA of the cleaned diff series with sqrt period
  const wmaDiff = WMA(cleanDiff, sqrtPeriod);

  // Map back to original indices
  const result: (number | null)[] = new Array(values.length).fill(null);
  for (let j = 0; j < wmaDiff.length; j++) {
    if (wmaDiff[j] !== null && j < cleanIndices.length) {
      result[cleanIndices[j]] = wmaDiff[j];
    }
  }

  return result;
}

// ─── TMA — Triangular Moving Average ─────────────────────────────
// TMA(n) = SMA(SMA(close, n), n)

export function TMA(values: number[], period: number): (number | null)[] {
  if (period <= 0 || values.length === 0) return values.map(() => null);

  const firstSMA = SMA(values, period);

  // Extract non-null values from first SMA
  const cleanSMA: number[] = [];
  const cleanIndices: number[] = [];
  for (let i = 0; i < firstSMA.length; i++) {
    if (firstSMA[i] !== null) {
      cleanSMA.push(firstSMA[i]!);
      cleanIndices.push(i);
    }
  }

  // SMA of the SMA
  const secondSMA = SMA(cleanSMA, period);

  // Map back to original indices
  const result: (number | null)[] = new Array(values.length).fill(null);
  for (let j = 0; j < secondSMA.length; j++) {
    if (secondSMA[j] !== null && j < cleanIndices.length) {
      result[cleanIndices[j]] = secondSMA[j];
    }
  }

  return result;
}

// ─── Standard Deviation ──────────────────────────────────────────

export function standardDeviation(values: number[], mean: number): number {
  if (values.length === 0) return 0;
  const squaredDiffs = values.map((v) => (v - mean) ** 2);
  const variance = squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  return Math.sqrt(variance);
}

// ─── Bollinger Bands ─────────────────────────────────────────────

export function BollingerBands(
  values: number[],
  period = 20,
  deviations = 2
): BollingerResult {
  const middle = SMA(values, period);
  const upper: (number | null)[] = new Array(values.length).fill(null);
  const lower: (number | null)[] = new Array(values.length).fill(null);
  const bandwidth: (number | null)[] = new Array(values.length).fill(null);

  for (let i = period - 1; i < values.length; i++) {
    if (middle[i] !== null) {
      const slice = values.slice(i - period + 1, i + 1);
      const std = standardDeviation(slice, middle[i]!);
      upper[i] = middle[i]! + deviations * std;
      lower[i] = middle[i]! - deviations * std;
      // Bandwidth: (upper - lower) / middle * 100
      bandwidth[i] = middle[i]! !== 0 ? ((upper[i]! - lower[i]!) / middle[i]!) * 100 : null;
    }
  }

  return { middle, upper, lower, bandwidth };
}

// ─── Parse Trendbars from cTrader API response ───────────────────

export function parseTrendbars(raw: Record<string, unknown>, digits = 5): CandleData[] {
  let tbArray: Array<Record<string, unknown>> = [];
  if (Array.isArray(raw.trendbar)) {
    tbArray = raw.trendbar as Array<Record<string, unknown>>;
  } else if (Array.isArray(raw.trendbars)) {
    tbArray = raw.trendbars as Array<Record<string, unknown>>;
  } else if (Array.isArray(raw.data)) {
    tbArray = raw.data as Array<Record<string, unknown>>;
  } else if (typeof raw.trendbar === 'object' && raw.trendbar !== null) {
    tbArray = [raw.trendbar as Record<string, unknown>];
  } else if (typeof raw.trendbars === 'object' && raw.trendbars !== null && !Array.isArray(raw.trendbars)) {
    tbArray = [raw.trendbars as Record<string, unknown>];
  } else if (Array.isArray(raw)) {
    tbArray = raw as Array<Record<string, unknown>>;
  }

  // cTrader OpenAPI encodes int64 as strings in JSON. Try string→number coercion.
  function readNum(obj: Record<string, unknown>, keys: string[]): number | null {
    for (const key of keys) {
      const val = obj[key];
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const n = parseFloat(val);
        if (!isNaN(n)) return n;
      }
    }
    return null;
  }

  // Phase 1 — Raw parse: read absolute & delta fields from each trendbar
  const rawParsed = tbArray
    .map((tb) => {
      let ts = readNum(tb, ['timestamp', 'utcTimestampInMinutes', 'utcTimestamp', 'time', 't']);
      if (ts !== null && ts < 1e10) {
        ts = ts * 60000; // minutes → ms
      }

      let open = readNum(tb, ['open', 'openCents', 'o', 'openPrice', 'deltaOpen']);
      let high = readNum(tb, ['high', 'highCents', 'h', 'highPrice', 'deltaHigh']);
      let low = readNum(tb, ['low', 'lowCents', 'l', 'lowPrice', 'deltaLow']);
      let close = readNum(tb, ['close', 'closeCents', 'c', 'closePrice', 'deltaClose']);
      const volume = readNum(tb, ['volume', 'vol', 'tickVolume', 'v']);

      return {
        timestamp: ts ?? 0,
        open: open ?? 0,
        high: high ?? 0,
        low: low ?? 0,
        close: close ?? 0,
        volume: volume ?? undefined,
        _hasDelta: ('deltaOpen' in tb || 'deltaHigh' in tb || 'deltaClose' in tb),
      };
    });

  // Phase 2 — Delta decoding (per-bar, not all-or-nothing)
  // cTrader encodes trendbars with absolute `low` and per-bar deltas FROM low:
  //   deltaOpen = open - low (for this bar)
  //   deltaHigh = high - low (for this bar)
  //   deltaClose = close - low (for this bar)
  // Reconstruct: open = low + deltaOpen, high = low + deltaHigh, close = low + deltaClose
  for (let i = 0; i < rawParsed.length; i++) {
    const bar = rawParsed[i];
    const hasDelta = (bar as Record<string, unknown>)._hasDelta === true;
    if (hasDelta && bar.low > 0) {
      bar.open = bar.low + bar.open;
      bar.high = bar.low + bar.high;
      bar.close = bar.low + bar.close;
    }
  }

  // Phase 3 — Strip internal flag, apply price scaling using digits, filter & sort
  const divisor = Math.pow(10, digits);

  const result = rawParsed
    .map((p) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _hasDelta: __, ...rest } = p as Record<string, unknown>;
      return rest as unknown as CandleData;
    })
    .map((c) => ({
      timestamp: c.timestamp,
      open: c.open ? c.open / divisor : c.open,
      high: c.high ? c.high / divisor : c.high,
      low: c.low ? c.low / divisor : c.low,
      close: c.close ? c.close / divisor : c.close,
      volume: c.volume,
    }))
    .filter((c) => c.timestamp > 0 && c.close !== 0)
    .sort((a, b) => a.timestamp - b.timestamp);

  // Attach debug info so caller can log it — clean summary
  const closePrices = result.map((c) => c.close);
  (result as unknown as Record<string, unknown>)._parseDebug = {
    candleCount: result.length,
    deltaDetected: rawParsed.some((p) => (p as Record<string, unknown>)._hasDelta === true),
    scaling: { digits, divisor },
    range: result.length > 0
      ? {
          min: Math.min(...closePrices).toFixed(5),
          max: Math.max(...closePrices).toFixed(5),
          avg: (closePrices.reduce((a, b) => a + b, 0) / closePrices.length).toFixed(5),
        }
      : null,
  };

  return result;
}

// ─── Current (latest) indicator values ───────────────────────────

export interface CurrentIndicators {
  sma14: number | null;
  sma20: number | null;
  sma50: number | null;
  hma50: number | null;
  hma100: number | null;
  tma50: number | null;
  bbMiddle: number | null;
  bbUpper: number | null;
  bbLower: number | null;
  bbBandwidth: number | null;
  closePrice: number | null;
}

export function calculateCurrentIndicators(candles: CandleData[]): CurrentIndicators {
  const closes = candles.map((c) => c.close);
  const latestClose = closes.length > 0 ? closes[closes.length - 1] : null;

  const sma14 = SMA(closes, 14);
  const sma20 = SMA(closes, 20);
  const sma50 = SMA(closes, 50);
  const hma50 = HMA(closes, 50);
  const hma100 = HMA(closes, 100);
  const tma50 = TMA(closes, 50);
  const bb = BollingerBands(closes, 20, 2);

  const last = (arr: (number | null)[]) => (arr.length > 0 ? arr[arr.length - 1] : null);

  return {
    sma14: last(sma14),
    sma20: last(sma20),
    sma50: last(sma50),
    hma50: last(hma50),
    hma100: last(hma100),
    tma50: last(tma50),
    bbMiddle: last(bb.middle),
    bbUpper: last(bb.upper),
    bbLower: last(bb.lower),
    bbBandwidth: last(bb.bandwidth),
    closePrice: latestClose,
  };
}

// ─── Get closes from candles ─────────────────────────────────────

export function getCloses(candles: CandleData[]): number[] {
  return candles.map((c) => c.close);
}

// ─── Merge live trendbar into candle array (upsert by timestamp) ──
// Used to merge real-time trendbar SSE events into the historical candle array
// so indicators update on every live tick without re-fetching.

export function mergeLiveCandle(
  candles: CandleData[],
  newCandle: CandleData
): CandleData[] {
  // Find existing candle with same timestamp
  const existingIdx = candles.findIndex((c) => c.timestamp === newCandle.timestamp);

  if (existingIdx >= 0) {
    // Replace existing (current forming candle update)
    const updated = [...candles];
    updated[existingIdx] = newCandle;
    return updated;
  }

  // Append new candle and keep sorted
  const merged = [...candles, newCandle];
  merged.sort((a, b) => a.timestamp - b.timestamp);
  return merged;
}

// ─── Cross TMA50 Detector ────────────────────────────────────────
// Detects when close price crosses above or below TMA50 between two
// consecutive candles (A1 → A0 where A0 is the most recent closed candle).
//
// Returns true when:
//   - close crosses UP through TMA50   (A1 close ≤ TMA50 A1  AND  A0 close ≥ TMA50 A0)
//   - close crosses DOWN through TMA50 (A1 close ≥ TMA50 A1  AND  A0 close ≤ TMA50 A0)
// Returns false otherwise (including when not enough data or null indicators).

export function crossTMA50(candles: CandleData[], tma50: (number | null)[]): boolean {
  // Default: false
  if (candles.length < 2 || tma50.length < 2) return false;

  const a0Close = candles[candles.length - 1].close;
  const a1Close = candles[candles.length - 2].close;
  const a0Tma = tma50[tma50.length - 1];
  const a1Tma = tma50[tma50.length - 2];

  // Need valid indicator values for both candles
  if (a0Tma === null || a1Tma === null) return false;

  // Cross UP: A1 was below/at TMA50, A0 is above/at TMA50
  if (a1Close <= a1Tma && a0Close >= a0Tma) return true;

  // Cross DOWN: A1 was above/at TMA50, A0 is below/at TMA50
  if (a1Close >= a1Tma && a0Close <= a0Tma) return true;

  return false;
}

// ─── OIB — Outside Indicator Bands ───────────────────────────────
// Returns true when close price A0 is OUTSIDE the min-max range of
// the 6 indicator values (SMA14, SMA20, SMA50, HMA50, HMA100, TMA50).
//   - closePrice > max(indicator set) → true (price above all bands)
//   - closePrice < min(indicator set) → true (price below all bands)
//   - otherwise → false (price inside the band range)
// Returns false when fewer than 2 indicators have valid (non-null) values,
// because 1 value alone cannot define a meaningful band.

export function OIB(
  closePrice: number,
  sma14: number | null,
  sma20: number | null,
  sma50: number | null,
  hma50: number | null,
  hma100: number | null,
  tma50: number | null,
): boolean {
  // Default: false
  const values = [sma14, sma20, sma50, hma50, hma100, tma50].filter((v): v is number => v !== null);
  if (values.length < 2) return false;

  const maxVal = Math.max(...values);
  const minVal = Math.min(...values);

  return closePrice > maxVal || closePrice < minVal;
}

// ─── Elgufing (Engulfing Pattern) Detector ─────────────────────────
// Detects bullish/bearish engulfing patterns between two consecutive
// candles A1 (previous) → A0 (current/most recent closed candle).
//
// Returns true when:
//   - BEARISH engulfing (bullish A1 engulfed by bearish A0):
//     A1 open < A1 close (bullish) AND A0 open > A0 close (bearish)
//     AND A0 body >= 80% A1 body AND A1 body >= minPips
//   - BULLISH engulfing (bearish A1 engulfed by bullish A0):
//     A1 open > A1 close (bearish) AND A0 open < A0 close (bullish)
//     AND A0 body >= 80% A1 body AND A1 body >= minPips
// Returns false otherwise (default).
//
// Body = abs(open - close) / pipSize  (distance in pips)

export function elgufing(
  a0: { open: number; close: number },
  a1: { open: number; close: number },
  pipSize: number,
  minPips: number,
): boolean {
  // Default: false
  if (pipSize <= 0 || minPips < 0) return false;

  const a0Body = Math.abs(a0.open - a0.close) / pipSize;
  const a1Body = Math.abs(a1.open - a1.close) / pipSize;

  // Guard: A1 body must be >= minPips threshold
  if (a1Body < minPips) return false;

  // Guard: A0 body must be >= 80% of A1 body to qualify as engulfing
  if (a0Body < a1Body * 0.8) return false;

  const a1Bullish = a1.open < a1.close;
  const a0Bullish = a0.open < a0.close;

  // Bearish engulfing: A1 bullish, A0 bearish
  if (a1Bullish && !a0Bullish) return true;

  // Bullish engulfing: A1 bearish, A0 bullish
  if (!a1Bullish && a0Bullish) return true;

  return false;
}
// ─── End Elgufing ──────────────────────────────────────────────────